import ImageGalleryItem from '../store/schema/ImageGalleryItem';
import ImagePreviewState from '../store/schema/ImagePreviewState';
import SxSViewState from '../store/schema/SxSViewState';
import JsApiInfo from '../store/schema/JsApiInfo';
import PdfJsPreviewState, { PdfJsPasswordState } from '../store/schema/PdfJsPreviewState';
import { action } from 'satcheljs';
import { PreviewPaneViewStateBase } from '../store/schema/PreviewPaneViewState';

const resetPreviewPane = action('resetPreviewPane', (viewState: SxSViewState) => ({
    viewState,
}));

const setAudioPreviewState = action(
    'setAudioPreviewState',
    (
        viewState: SxSViewState,
        previewUrl: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        previewUrl,
        optionalParameters,
    })
);

const setBlankPreviewState = action(
    'setBlankPreviewState',
    (viewState: SxSViewState, optionalParameters?: PreviewPaneViewStateBase) => ({
        viewState,
        optionalParameters,
    })
);

const setCustomPreviewState = action(
    'setCustomPreviewState',
    (viewState: SxSViewState, optionalParameters?: PreviewPaneViewStateBase) => ({
        viewState,
        optionalParameters,
    })
);

const setIframePreviewState = action(
    'setIframePreviewState',
    (
        viewState: SxSViewState,
        previewUrl: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        previewUrl,
        optionalParameters,
    })
);

const setImagePreviewState = action(
    'setImagePreviewState',
    (
        viewState: SxSViewState,
        previewUrl: string,
        imageGalleryItems: ImageGalleryItem[],
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        previewUrl,
        imageGalleryItems,
        optionalParameters,
    })
);

const setImagePreviewStateFromImageGalleryItem = action(
    'setImagePreviewStateFromImageGalleryItem',
    (imagePreviewState: ImagePreviewState, imageGalleryItem: ImageGalleryItem) => ({
        imagePreviewState,
        imageGalleryItem,
    })
);

const setNativePdfPreviewState = action(
    'setNativePdfPreviewState',
    (
        viewState: SxSViewState,
        previewUrl: string,
        printUrl: string | null,
        mediaType: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        previewUrl,
        printUrl,
        mediaType,
        optionalParameters,
    })
);

const setPdfJsPreviewState = action(
    'setPdfJsPreviewState',
    (
        viewState: SxSViewState,
        previewUrl: string,
        printDisabled: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        previewUrl,
        printDisabled,
        optionalParameters,
    })
);

const setPdfJsPreviewStateByData = action(
    'setPdfJsPreviewStateByData',
    (
        viewState: SxSViewState,
        data: Uint8Array,
        printDisabled: boolean,
        copyAllowed: boolean,
        exportAllowed: boolean,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        data,
        printDisabled,
        copyAllowed,
        exportAllowed,
        optionalParameters,
    })
);

const setPreviewError = action(
    'setPreviewError',
    (
        viewState: SxSViewState,
        errorMessage: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        errorMessage,
        optionalParameters,
    })
);

const setPreviewLoading = action(
    'setPreviewLoading',
    (viewState: SxSViewState, message: string, optionalParameters?: PreviewPaneViewStateBase) => ({
        viewState,
        message,
        optionalParameters,
    })
);

const setTextPreviewState = action(
    'setTextPreviewState',
    (viewState: SxSViewState, text: string, optionalParameters?: PreviewPaneViewStateBase) => ({
        viewState,
        text,
        optionalParameters,
    })
);

const setSaveStatusText = action('setSaveStatusText', (viewState: SxSViewState, text: string) => ({
    viewState,
    text,
}));

const setVideoPreviewState = action(
    'setVideoPreviewState',
    (
        viewState: SxSViewState,
        previewUrl: string,
        optionalParameters?: PreviewPaneViewStateBase
    ) => ({
        viewState,
        previewUrl,
        optionalParameters,
    })
);

const setWacPreviewState = action('setWacPreviewState', (
    viewState: SxSViewState,
    wacUrl: string,
    jsApiInfo?: JsApiInfo,
    accessToken?: string,
    accessTokenTtl?: string,
    additionalParameters?: string,
    deprecatedTokenAndParameters?: string,
    clickTime?: number, // time of user action that triggered the preview
    readOnly?: boolean,
    optionalParameters?: PreviewPaneViewStateBase
) => ({
    viewState,
    wacUrl,
    jsApiInfo,
    accessToken,
    accessTokenTtl,
    additionalParameters,
    deprecatedTokenAndParameters,
    clickTime,
    readOnly,
    optionalParameters,
}));

const setWacReadOnly = action('setWacReadOnly', (viewState: SxSViewState, readOnly: boolean) => ({
    viewState,
    readOnly,
}));

const setPdfJsPasswordState = action(
    'setPdfJsPasswordState',
    (viewState: PdfJsPreviewState, passwordState: PdfJsPasswordState) => ({
        viewState,
        passwordState,
    })
);

export {
    resetPreviewPane,
    setAudioPreviewState,
    setBlankPreviewState,
    setCustomPreviewState,
    setIframePreviewState,
    setImagePreviewState,
    setImagePreviewStateFromImageGalleryItem,
    setNativePdfPreviewState,
    setPdfJsPasswordState,
    setPdfJsPreviewState,
    setPdfJsPreviewStateByData,
    setPreviewError,
    setPreviewLoading,
    setSaveStatusText,
    setTextPreviewState,
    setVideoPreviewState,
    setWacPreviewState,
    setWacReadOnly,
};
